import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { cn } from '../lib/utils';

const MAX_VISIBLE_ITEMS = 80;

const SearchableComboBox = ({
  value = '',
  onChange,
  options = [],
  placeholder = 'Pilih data...',
  searchPlaceholder = 'Cari...',
  emptyLabel = 'Data tidak ditemukan',
  disabled = false,
  className = '',
  includeEmpty = false,
  emptyValueLabel = 'Kosongkan pilihan',
  onSearch = null,
  searchDebounceMs = 300,
  minSearchChars = 0,
  enableSearchCache = true,
  searchContextKey = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const searchCacheRef = React.useRef(new Set());

  const normalizedOptions = useMemo(() => {
    return options
      .map((item) => ({
        value: String(item?.value || '').trim(),
        label: String(item?.label || '').trim(),
        description: String(item?.description || '').trim(),
        keywords: String(item?.keywords || '').trim(),
      }))
      .filter((item) => item.value && item.label);
  }, [options]);

  const selectedItem = useMemo(() => {
    return normalizedOptions.find((item) => item.value === String(value || '').trim());
  }, [normalizedOptions, value]);

  const visibleOptions = useMemo(() => normalizedOptions.slice(0, MAX_VISIBLE_ITEMS), [normalizedOptions]);
  const hasMoreItems = normalizedOptions.length > MAX_VISIBLE_ITEMS;

  React.useEffect(() => {
    if (!open || typeof onSearch !== 'function') return undefined;

    const timer = setTimeout(async () => {
      const normalizedQuery = String(query || '').trim();
      const minChars = Math.max(0, Number(minSearchChars || 0));
      if (normalizedQuery.length > 0 && normalizedQuery.length < minChars) return;

      const cacheKey = `${String(searchContextKey || '')}::${normalizedQuery}`;
      if (enableSearchCache && searchCacheRef.current.has(cacheKey)) return;

      try {
        setSearching(true);
        await onSearch(normalizedQuery);
        if (enableSearchCache) {
          searchCacheRef.current.add(cacheKey);
        }
      } finally {
        setSearching(false);
      }
    }, Math.max(100, Number(searchDebounceMs || 300)));

    return () => clearTimeout(timer);
  }, [open, query, onSearch, searchDebounceMs, minSearchChars, enableSearchCache, searchContextKey]);

  React.useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  React.useEffect(() => {
    searchCacheRef.current.clear();
  }, [searchContextKey]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between bg-[#1a1a1a] border-white/10 text-white hover:bg-[#222] hover:text-white',
            className
          )}
          disabled={disabled}
        >
          <span className="truncate text-left">{selectedItem?.label || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-[#111] border-white/10 z-[120]">
        <Command className="bg-[#111] text-white">
          <CommandInput
            placeholder={searchPlaceholder}
            className="text-white"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {includeEmpty && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange?.('');
                    setOpen(false);
                  }}
                  className="text-gray-300"
                >
                  <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                  <span>{emptyValueLabel}</span>
                </CommandItem>
              )}
              {visibleOptions.map((item) => {
                const commandValue = `${item.label} ${item.keywords} ${item.description}`.trim();
                return (
                  <CommandItem
                    key={item.value}
                    value={commandValue}
                    onSelect={() => {
                      onChange?.(item.value);
                      setOpen(false);
                    }}
                    className="text-white"
                  >
                    <Check className={cn('mr-2 h-4 w-4', item.value === value ? 'opacity-100' : 'opacity-0')} />
                    <div className="min-w-0">
                      <div className="truncate">{item.label}</div>
                      {item.description ? <div className="truncate text-xs text-gray-400">{item.description}</div> : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {searching ? <p className="px-3 py-2 text-[11px] text-cyan-300">Memuat hasil...</p> : null}
          {!searching && Number(minSearchChars || 0) > 0 && query.trim().length > 0 && query.trim().length < Number(minSearchChars || 0) ? (
            <p className="px-3 py-2 text-[11px] text-gray-400">
              Ketik minimal {Number(minSearchChars || 0)} karakter untuk pencarian server.
            </p>
          ) : null}
          {hasMoreItems ? (
            <p className="border-t border-white/10 px-3 py-2 text-[11px] text-gray-400">
              Menampilkan {MAX_VISIBLE_ITEMS} dari {normalizedOptions.length} data. Gunakan kata kunci spesifik agar hasil lebih cepat.
            </p>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableComboBox;
